import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const handleClick = (item: string) => {
    console.log(`Clicked on ${item}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <motion.header 
      className={`fixed top-4 left-0 right-0 mx-auto w-11/12 max-w-6xl z-50 transition-all duration-300 liquid-glass rounded-full py-3 px-6 ${isScrolled ? 'bg-black/80 backdrop-blur-md shadow-lg shadow-gold-900/50 border border-gold-800/30' : 'bg-black/50 border border-gold-900/10'}`}
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: 'easeOut', type: 'spring', stiffness: 120 }}
      whileHover={{ y: -5, boxShadow: '0 10px 30px -10px rgba(211, 158, 61, 0.5)' }}
      whileInView={{ scale: 1.02 }}
    >
      <div className="flex items-center justify-between h-12 relative">
        {/* Logo - Left */}
        <div className="flex-1 flex items-center relative z-10">
          <motion.div 
            className="flex items-center"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link to="/" className="relative">
              {/* Desktop Logo */}
              <img 
                src="/setora-logo-v1.png" 
                alt="Setora Logo" 
                className="hidden sm:block h-10 w-auto object-contain"
              />
              {/* Mobile Logo */}
              <img 
                src="/setora-logo-v2.png" 
                alt="Setora Logo" 
                className="block sm:hidden h-8 w-auto object-contain"
              />
              {isScrolled && (
                <motion.div
                  className="absolute inset-0 opacity-50"
                  animate={{
                    backgroundPosition: ['200% 0%', '-200% 0%'],
                    filter: 'drop-shadow(0 0 15px rgba(255, 215, 0, 0.8))',
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: 'linear'
                  }}
                  style={{ 
                    backgroundSize: '200% 200%',
                    background: 'linear-gradient(105deg, transparent 40%, rgba(255, 215, 0, 0.4) 50%, transparent 60%)',
                  }}
                />
              )}
            </Link>
          </motion.div>
        </div>
        {/* Gold reflection effect on scroll */}
        <motion.div 
          className="absolute inset-0 rounded-full overflow-hidden opacity-0 z-0 pointer-events-none"
          animate={{ 
            opacity: isScrolled ? 0.3 : 0,
          }}
          transition={{ 
            opacity: { duration: 0.5 },
          }}
        >
          <motion.div
            className="absolute inset-0"
            animate={{
              backgroundPosition: ['200% 0%', '-200% 0%'],
              filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.6))',
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'linear'
            }}
            style={{
              background: 'linear-gradient(105deg, transparent 40%, rgba(255, 215, 0, 0.4) 50%, transparent 60%)',
              backgroundSize: '200% 200%',
            }}
          />
        </motion.div>
        {/* Navigation - Center (Desktop) */}
        <nav className="hidden sm:flex flex-1 justify-center relative z-10">
          <ul className="flex space-x-8 items-center">
            <motion.li 
              className="list-none"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              whileHover={{ y: -3, scale: 1.1, rotate: 2 }}
            >
              <Link 
                to="/" 
                className={`text-sm font-medium transition-all relative ${isActive('/') ? 'scale-105' : ''}`}
                onClick={() => handleClick('Home')}
              >
                <span className={`bg-gradient-to-br ${isActive('/') ? 'from-[#FFD700] via-[#DAA520] to-[#B8860B]' : 'from-gray-300 to-gray-400'} bg-clip-text text-transparent ${isActive('/') ? '' : 'hover:from-[#FFD700] hover:via-[#DAA520] hover:to-[#B8860B]'}`}>
                  Home
                </span>
                {isScrolled && isActive('/') && (
                  <motion.span 
                    className="absolute inset-0 bg-gradient-to-r from-[#FFF8DC] via-transparent to-transparent bg-clip-text text-transparent opacity-60"
                    animate={{
                      x: ['-100%', '100%'],
                      boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'linear'
                    }}
                  >
                    Home
                  </motion.span>
                )}
              </Link>
            </motion.li>
            <motion.li 
              className="list-none"
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              whileHover={{ y: -3, scale: 1.1, rotate: 2 }}
            >
              <Link 
                to="/about" 
                className={`text-sm font-medium transition-all relative ${isActive('/about') ? 'scale-105' : ''}`}
                onClick={() => handleClick('About')}
              >
                <span className={`bg-gradient-to-br ${isActive('/about') ? 'from-[#FFD700] via-[#DAA520] to-[#B8860B]' : 'from-gray-300 to-gray-400'} bg-clip-text text-transparent ${isActive('/about') ? '' : 'hover:from-[#FFD700] hover:via-[#DAA520] hover:to-[#B8860B]'}`}>
                  About
                </span>
                {isScrolled && isActive('/about') && (
                  <motion.span 
                    className="absolute inset-0 bg-gradient-to-r from-[#FFF8DC] via-transparent to-transparent bg-clip-text text-transparent opacity-60"
                    animate={{
                      x: ['-100%', '100%'],
                      boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'linear'
                    }}
                  >
                    About
                  </motion.span>
                )}
              </Link>
            </motion.li>
            <motion.li
              className="list-none"
              initial={{ x: 35, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.25, duration: 0.8 }}
              whileHover={{ y: -3, scale: 1.1, rotate: 2 }}
            >
              <a
                href="/#services"
                className="text-sm font-medium transition-all relative"
                onClick={() => handleClick('Services')}
              >
                <span className="bg-gradient-to-br from-gray-300 to-gray-400 bg-clip-text text-transparent hover:from-[#FFD700] hover:via-[#DAA520] hover:to-[#B8860B]">
                  Services
                </span>
              </a>
            </motion.li>
            <motion.li
              className="list-none"
              initial={{ x: 38, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              whileHover={{ y: -3, scale: 1.1, rotate: 2 }}
            >
              <a
                href="/#work"
                className="text-sm font-medium transition-all relative"
                onClick={() => handleClick('Work')}
              >
                <span className="bg-gradient-to-br from-gray-300 to-gray-400 bg-clip-text text-transparent hover:from-[#FFD700] hover:via-[#DAA520] hover:to-[#B8860B]">
                  Work
                </span>
              </a>
            </motion.li>
            <motion.li
              className="list-none"
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.35, duration: 0.8 }}
              whileHover={{ y: -3, scale: 1.1, rotate: 2 }}
            >
              <Link
                to="/apps"
                className={`text-sm font-medium transition-all relative ${isActive('/apps') ? 'scale-105' : ''}`}
                onClick={() => handleClick('Apps')}
              >
                <span className={`bg-gradient-to-br ${isActive('/apps') ? 'from-[#FFD700] via-[#DAA520] to-[#B8860B]' : 'from-gray-300 to-gray-400'} bg-clip-text text-transparent ${isActive('/apps') ? '' : 'hover:from-[#FFD700] hover:via-[#DAA520] hover:to-[#B8860B]'}`}>
                  Apps
                </span>
                {isScrolled && isActive('/apps') && (
                  <motion.span 
                    className="absolute inset-0 bg-gradient-to-r from-[#FFF8DC] via-transparent to-transparent bg-clip-text text-transparent opacity-60"
                    animate={{
                      x: ['-100%', '100%'],
                      boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'linear'
                    }}
                  >
                    Apps
                  </motion.span>
                )}
              </Link>
            </motion.li>
            <motion.li
              className="list-none"
              initial={{ x: 70, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              whileHover={{ y: -3, scale: 1.1, rotate: 2 }}
            >
              <Link 
                to="/contact" 
                className={`text-sm font-medium transition-all relative ${isActive('/contact') ? 'scale-105' : ''}`}
                onClick={() => handleClick('Contact')}
              >
                <span className={`bg-gradient-to-br ${isActive('/contact') ? 'from-[#FFD700] via-[#DAA520] to-[#B8860B]' : 'from-gray-300 to-gray-400'} bg-clip-text text-transparent ${isActive('/contact') ? '' : 'hover:from-[#FFD700] hover:via-[#DAA520] hover:to-[#B8860B]'}`}>
                  Contact
                </span>
                {isScrolled && isActive('/contact') && (
                  <motion.span 
                    className="absolute inset-0 bg-gradient-to-r from-[#FFF8DC] via-transparent to-transparent bg-clip-text text-transparent opacity-60"
                    animate={{
                      x: ['-100%', '100%'],
                      boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'linear'
                    }}
                  >
                    Contact
                  </motion.span>
                )}
              </Link>
            </motion.li>
          </ul>
        </nav>

        {/* Get Started Button - Right (Desktop) */}
        <div className="hidden sm:flex flex-1 justify-end items-center relative z-10">
          <motion.a
            href="/contact"
            className="inline-flex items-center px-4 py-2 border border-[#DAA520]/50 rounded-full text-sm font-medium relative overflow-hidden group"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            whileHover={{ scale: 1.05, filter: 'brightness(1.2)' }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="bg-gradient-to-br from-[#FFD700] via-[#DAA520] to-[#B8860B] bg-clip-text text-transparent relative z-10">
              Start a Project
            </span>
            {isScrolled && (
              <motion.div
                className="absolute inset-0 opacity-30"
                animate={{
                  backgroundPosition: ['200% 0%', '-200% 0%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear'
                }}
                style={{
                  background: 'linear-gradient(105deg, transparent 40%, rgba(255, 248, 220, 0.6) 50%, transparent 60%)',
                  backgroundSize: '200% 200%',
                }}
              />
            )}
          </motion.a>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex items-center sm:hidden">
          <button 
            onClick={toggleMenu}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-300 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gold-400 transition-all duration-300"
          >
            <span className="sr-only">Open main menu</span>
            {/* Icon for menu toggle */}
            <svg 
              className={`${isMenuOpen ? 'hidden' : 'block'} h-6 w-6`} 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg 
              className={`${isMenuOpen ? 'block' : 'hidden'} h-6 w-6`} 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`${isMenuOpen ? 'block' : 'hidden'} sm:hidden`}>
        <div className="pt-2 pb-3 space-y-1 bg-black/90 backdrop-blur-md border-t border-gold-900/30">
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <Link 
              to="/" 
              className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/') ? 'text-gold-400 bg-gray-900/30' : 'text-gray-300 hover:text-gold-300 hover:bg-gray-900/20'} transition-colors`}
              onClick={() => { console.log('Navigating to Home from mobile menu'); closeMenu(); }}
            >
              Home
            </Link>
          </motion.div>
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Link 
              to="/about" 
              className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/about') ? 'text-gold-400 bg-gray-900/30' : 'text-gray-300 hover:text-gold-300 hover:bg-gray-900/20'} transition-colors`}
              onClick={() => { console.log('Navigating to About from mobile menu'); closeMenu(); }}
            >
              About
            </Link>
          </motion.div>
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Link 
              to="/apps" 
              className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/apps') ? 'text-gold-400 bg-gray-900/30' : 'text-gray-300 hover:text-gold-300 hover:bg-gray-900/20'} transition-colors`}
              onClick={() => { console.log('Navigating to Apps from mobile menu'); closeMenu(); }}
            >
              Apps
            </Link>
          </motion.div>
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.5 }}
          >
            <a
              href="/#services"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-gold-300 hover:bg-gray-900/20 transition-colors"
              onClick={closeMenu}
            >
              Services
            </a>
          </motion.div>
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <a
              href="/#work"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-gold-300 hover:bg-gray-900/20 transition-colors"
              onClick={closeMenu}
            >
              Work
            </a>
          </motion.div>
          <motion.div
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <Link
              to="/contact"
              className={`block px-3 py-2 rounded-md text-base font-medium ${isActive('/contact') ? 'text-gold-400 bg-gray-900/30' : 'text-gray-300 hover:text-gold-300 hover:bg-gray-900/20'} transition-colors`}
              onClick={() => { console.log('Navigating to Contact from mobile menu'); closeMenu(); }}
            >
              Contact
            </Link>
          </motion.div>
        </div>
        <div className="pt-4 pb-3 border-t border-gold-900/30 bg-black/90 backdrop-blur-md">
          <motion.div 
            className="mt-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.5 }}
          >
            <Link
              to="/contact"
              onClick={closeMenu}
              className="w-full flex justify-center px-3 py-2 text-white bg-gold-400/20 hover:bg-gold-400/30 rounded-md font-medium transition-colors duration-300 border border-gold-700/30 hover:border-gold-600/50"
            >
              Start a Project
            </Link>
          </motion.div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header; 